// src/pages/portal/PortalTicketsPage.tsx
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePortalTickets, usePortalEmpresas } from '@/hooks/queries';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  EmptyState,
} from '@/components/ui';
import { Plus, Ticket, Eye } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

function TicketsTableSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-16" />
      ))}
    </div>
  );
}

export default function PortalTicketsPage() {
  const navigate = useNavigate();
  const [empresaId, setEmpresaId] = useState<string>('');
  const [estado, setEstado] = useState<string>('');

  const { data: empresas } = usePortalEmpresas();
  const { data: ticketsData, isLoading, isError } = usePortalTickets({
    empresa_id: empresaId && empresaId !== '__all__' ? empresaId : undefined,
    estado: estado && estado !== '__all__' ? estado : undefined,
  });

  const tickets = ticketsData?.items || [];

  const estadoBadgeVariant = (estado: string) => {
    switch (estado?.toLowerCase()) {
      case 'abierto':
      case 'nuevo':
        return 'info';
      case 'en_progreso':
      case 'en progreso':
        return 'warning';
      case 'resuelto':
        return 'success';
      case 'cerrado':
        return 'default';
      default:
        return 'default';
    }
  };

  const prioridadBadgeVariant = (prioridad: string) => {
    switch (prioridad?.toLowerCase()) {
      case 'alta':
      case 'urgente':
        return 'danger';
      case 'media':
        return 'warning';
      case 'baja':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Mis Tickets
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gestiona tus solicitudes de soporte
          </p>
        </div>
        <Button onClick={() => navigate('/portal/tickets/nuevo')}>
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Ticket
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {empresas && empresas.length > 1 && (
              <Select value={empresaId} onValueChange={setEmpresaId}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Todas las empresas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas las empresas</SelectItem>
                  {empresas.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los estados</SelectItem>
                <SelectItem value="abierto">Abierto</SelectItem>
                <SelectItem value="en_progreso">En Progreso</SelectItem>
                <SelectItem value="resuelto">Resuelto</SelectItem>
                <SelectItem value="cerrado">Cerrado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Lista de tickets */}
      <Card>
        <CardHeader>
          <CardTitle>Tickets ({ticketsData?.total || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <TicketsTableSkeleton />
          ) : isError ? (
            <div className="text-center py-8">
              <p className="text-red-600 dark:text-red-400">Error al cargar los tickets</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Intenta recargar la pagina</p>
            </div>
          ) : tickets.length === 0 ? (
            <EmptyState
              icon={Ticket}
              title="No tienes tickets"
              description="Crea un nuevo ticket para reportar un problema o solicitar ayuda."
              action={
                <Button onClick={() => navigate('/portal/tickets/nuevo')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Ticket
                </Button>
              }
            />
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/portal/tickets/${ticket.id}`}
                  className="block p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          #{ticket.numero}
                        </span>
                        <Badge variant={estadoBadgeVariant(ticket.estado)}>
                          {ticket.estado}
                        </Badge>
                        <Badge variant={prioridadBadgeVariant(ticket.prioridad)}>
                          {ticket.prioridad}
                        </Badge>
                      </div>
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">
                        {ticket.titulo}
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                        <span>{ticket.tipo}</span>
                        {ticket.empresa && <span>{ticket.empresa.nombre}</span>}
                        <span>
                          {formatDistanceToNow(new Date(ticket.fecha_creacion), {
                            addSuffix: true,
                            locale: es,
                          })}
                        </span>
                        {ticket.comentarios_count > 0 && (
                          <span>{ticket.comentarios_count} comentarios</span>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
